'use client';

import AppLayout from '@/components/AppLayout';
import { mockGuitars } from '@/app/guitar-collection/components/mockData';
import { Wrench, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

interface MaintenanceRecord {
  id: string;
  guitarId: string;
  brand: string;
  model: string;
  type: 'Maintenance' | 'String Change' | 'Setup' | 'Repair';
  date: string;
  status: 'overdue' | 'due-soon' | 'ok';
  notes: string;
}

function getDaysDiff(dateStr: string): number {
  const [month, day, year] = dateStr.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  const now = new Date(2026, 4, 14); // fixed date to avoid hydration issues
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
}

function getStatus(daysSince: number): 'overdue' | 'due-soon' | 'ok' {
  if (daysSince > 180) return 'overdue';
  if (daysSince > 90) return 'due-soon';
  return 'ok';
}

const statusConfig = {
  overdue: { label: 'Overdue', color: 'text-red-600 bg-red-50 border-red-200', icon: AlertTriangle },
  'due-soon': { label: 'Due Soon', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock },
  ok: { label: 'Up to Date', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle },
};

export default function MaintenancePage() {
  const records: MaintenanceRecord[] = mockGuitars.flatMap((g) => [
    {
      id: `${g.id}-maint`,
      guitarId: g.id,
      brand: g.brand,
      model: g.model,
      type: 'Maintenance' as const,
      date: g.lastMaintenance,
      status: getStatus(getDaysDiff(g.lastMaintenance)),
      notes: g.notes,
    },
    {
      id: `${g.id}-strings`,
      guitarId: g.id,
      brand: g.brand,
      model: g.model,
      type: 'String Change' as const,
      date: g.lastStringChange,
      status: getStatus(getDaysDiff(g.lastStringChange)),
      notes: '',
    },
  ]);

  const overdue = records.filter((r) => r.status === 'overdue').length;
  const dueSoon = records.filter((r) => r.status === 'due-soon').length;
  const upToDate = records.filter((r) => r.status === 'ok').length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Maintenance</h1>
          <p className="text-sm text-muted-foreground mt-1">Track maintenance and string changes for all guitars</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle size={20} className="text-red-600 shrink-0" />
            <div>
              <p className="text-2xl font-bold text-red-700">{overdue}</p>
              <p className="text-xs text-red-600 font-medium">Overdue</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <Clock size={20} className="text-amber-600 shrink-0" />
            <div>
              <p className="text-2xl font-bold text-amber-700">{dueSoon}</p>
              <p className="text-xs text-amber-600 font-medium">Due Soon</p>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle size={20} className="text-green-600 shrink-0" />
            <div>
              <p className="text-2xl font-bold text-green-700">{upToDate}</p>
              <p className="text-xs text-green-600 font-medium">Up to Date</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Wrench size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Maintenance Log</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Guitar</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Days Ago</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {records
                  .sort((a, b) => {
                    const order = { overdue: 0, 'due-soon': 1, ok: 2 };
                    return order[a.status] - order[b.status];
                  })
                  .map((record) => {
                    const days = getDaysDiff(record.date);
                    const cfg = statusConfig[record.status];
                    const StatusIcon = cfg.icon;
                    return (
                      <tr key={record.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-3">
                          <p className="font-medium text-foreground">{record.brand}</p>
                          <p className="text-xs text-muted-foreground">{record.model}</p>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {record.type}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">{record.date}</td>
                        <td className="px-5 py-3 text-muted-foreground">{days}d</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${cfg.color}`}>
                            <StatusIcon size={11} />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground max-w-xs truncate">{record.notes || '—'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
